import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Shield, Eye, EyeOff, DollarSign, Users, Lock, History,
} from "lucide-react";
import { ROLES, type UserRole } from "@/data/RoleContext";
import {
  getFinancialPermissions,
  PERMISSION_DESCRIPTIONS,
  type FinancialPermissions,
} from "@/data/financialPermissions";

const PERMISSION_KEYS: (keyof FinancialPermissions)[] = [
  "viewContractValue",
  "viewInternalCost",
  "viewMargin",
  "viewCrewPayout",
  "viewFinancialSummary",
  "viewCustomerPaymentStatus",
  "viewChangeOrderValue",
  "viewCompanyRevenue",
  "viewPricingLogic",
  "viewCredits",
];

const PERMISSION_LABELS: Record<keyof FinancialPermissions, string> = {
  viewContractValue: "Contract Value",
  viewInternalCost: "Internal Cost",
  viewMargin: "Margin",
  viewCrewPayout: "Crew Payout",
  viewFinancialSummary: "Financial Summary",
  viewCustomerPaymentStatus: "Payment Status",
  viewChangeOrderValue: "Change Order Value",
  viewCompanyRevenue: "Company Revenue",
  viewPricingLogic: "Pricing Logic",
  viewCredits: "Credits",
  viewWarehouseInternals: "Warehouse Internals",
  viewDispatchInternals: "Dispatch Internals",
  viewPartnerDistributions: "Partner Distributions",
  viewRepLeaderboard: "Rep Leaderboard",
  viewInternalNotes: "Internal Notes",
  scopedToOwn: "Scoped to Own Records",
};

const ROLE_TIERS: { label: string; roles: UserRole[]; tier: "full" | "limited" | "restricted" }[] = [
  { label: "Full Access", tier: "full", roles: ["super_admin", "owner", "cfo", "finance_manager"] },
  { label: "Limited Access", tier: "limited", roles: ["bookkeeper", "ops_manager", "admin", "sales", "head_of_installation", "procurement_manager", "inventory_manager"] },
  { label: "Restricted", tier: "restricted", roles: ["installer", "subcontractor", "warehouse_manager"] },
];

const AUDIT_LOG = [
  { user: "Admin", action: "Enabled 'View Margin' for Ops Manager", date: "2025-03-25 14:30" },
  { user: "Owner", action: "Disabled 'View Internal Cost' for Sales Rep", date: "2025-03-20 09:15" },
  { user: "Admin", action: "Financial Visibility system initialized", date: "2025-03-15 11:00" },
];

export function FinancialVisibilitySettings() {
  const [selectedRole, setSelectedRole] = useState<UserRole>("installer");
  const permissions = getFinancialPermissions(selectedRole);
  const roleInfo = ROLES.find((r) => r.key === selectedRole)!;

  const tierColor = (tier: string) =>
    tier === "full" ? "text-success bg-success/10 border-success/20" :
    tier === "limited" ? "text-warning bg-warning/10 border-warning/20" :
    "text-destructive bg-destructive/10 border-destructive/20";

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-bold">Financial Visibility Controls</h2>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Control what financial data each role can see across all modules, views, and portals.
            Field Crew and Subcontractors see only their payout information by default. Internal terms like
            cost, margin, and revenue are replaced with crew-friendly language for execution roles.
          </p>
        </CardContent>
      </Card>

      {/* Role Tiers Overview */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3 pb-1.5 border-b">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Access Tiers</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ROLE_TIERS.map((tier) => (
              <div key={tier.label} className={`rounded-lg border p-3 ${tierColor(tier.tier)}`}>
                <p className="text-xs font-bold mb-1.5">{tier.label}</p>
                <div className="flex flex-wrap gap-1">
                  {tier.roles.map((r) => {
                    const info = ROLES.find((ri) => ri.key === r);
                    return (
                      <button
                        key={r}
                        onClick={() => setSelectedRole(r)}
                        className={`text-[10px] rounded-md px-1.5 py-0.5 border transition-all ${
                          selectedRole === r
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background/50 border-border hover:border-primary/30"
                        }`}
                      >
                        {info?.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Role Detail Permissions */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">
                {roleInfo.label} — Financial Permissions
              </h3>
            </div>
            <Badge variant="outline" className="text-[10px]">{roleInfo.title}</Badge>
          </div>

          <div className="space-y-1">
            {PERMISSION_KEYS.map((key) => {
              const allowed = permissions[key];
              return (
                <div key={key} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    {allowed ? (
                      <Eye className="h-3.5 w-3.5 text-success shrink-0" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-medium">{PERMISSION_LABELS[key]}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{PERMISSION_DESCRIPTIONS[key]}</p>
                    </div>
                  </div>
                  <Switch
                    checked={allowed}
                    onCheckedChange={() => {
                      toast.info("Permission changes require admin approval and will be logged.", { duration: 3000 });
                    }}
                    className="shrink-0"
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Crew Language Preview */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Crew-Facing Language</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            For Field Crew and Subcontractor roles, internal financial terms are automatically replaced:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { from: "Contract Value", to: "Install Payout" },
              { from: "Financial Summary", to: "Compensation" },
              { from: "Net Value", to: "Earned Amount" },
              { from: "Change Orders", to: "Scope Adjustments" },
              { from: "Sub Payout", to: "Your Payout" },
              { from: "Original Contract", to: "Approved Payout" },
            ].map((item) => (
              <div key={item.from} className="rounded-lg border p-2 bg-muted/20">
                <p className="text-[10px] text-muted-foreground line-through">{item.from}</p>
                <p className="text-xs font-medium text-primary">{item.to}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
            <History className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Permission Change Log</h3>
          </div>
          <div className="space-y-2">
            {AUDIT_LOG.map((entry, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5 text-xs">
                <span className="text-[10px] text-muted-foreground shrink-0 w-28">{entry.date}</span>
                <span className="text-muted-foreground">·</span>
                <span className="font-medium shrink-0">{entry.user}</span>
                <span className="text-muted-foreground">—</span>
                <span className="text-muted-foreground">{entry.action}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
